// src/context/UIContext.tsx
import React, { createContext, useCallback, useContext, useMemo, useState, ReactNode } from 'react';
import LoadingOverlay from '../components/common/LoadingOverlay';
import ConfirmDialogModal from '../components/common/ConfirmDialog';

export type SnackbarPosition = 'default' | 'center';

export type SnackbarShowOptions = {
  duration?: number;
  position?: SnackbarPosition;
};

interface Snackbar {
  open: boolean;
  message: string;
  severity: 'success' | 'info' | 'warning' | 'error';
  duration?: number;
  position?: SnackbarPosition;
}

interface ConfirmDialog {
  open: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  severity?: 'warning' | 'error';
}

interface UIContextState {
  loading: boolean;
  snackbar: Snackbar;
  confirmDialog: ConfirmDialog;
  setLoading: (isLoading: boolean) => void;
  showLoading: (message?: string) => void;
  updateLoadingMessage: (message: string) => void;
  hideLoading: () => void;
  showSnackbar: (
    message: string,
    severity?: Snackbar['severity'],
    durationOrOptions?: number | SnackbarShowOptions
  ) => void;
  hideSnackbar: () => void;
  showConfirmDialog: (
    title: string,
    message: string,
    onConfirm: () => void,
    onCancel?: () => void,
    options?: { confirmText?: string; cancelText?: string; severity?: ConfirmDialog['severity'] }
  ) => void;
  hideConfirmDialog: () => void;
}

const defaultConfirmDialog: ConfirmDialog = {
  open: false,
  title: '',
  message: '',
  confirmText: 'Confirm',
  cancelText: 'Cancel',
  onConfirm: () => {},
  onCancel: () => {},
  severity: undefined,
};

// Create context with default values
const UIContext = createContext<UIContextState | undefined>(undefined);

// Custom hook for using the UI context
export const useUI = () => {
  const context = useContext(UIContext);
  if (context === undefined) {
    throw new Error('useUI must be used within a UIProvider');
  }
  return context;
};

// Provider component
export const UIProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<Snackbar>({
    open: false,
    message: '',
    severity: 'info',
    duration: 6000,
    position: 'default',
  });
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialog>(defaultConfirmDialog);

  // The actions below only call state setters, so they keep one identity for
  // the provider's lifetime. Consumers list them as effect dependencies; a new
  // function on every render re-ran those effects after each snackbar.
  const showSnackbar = useCallback((
    message: string,
    severity: Snackbar['severity'] = 'info',
    durationOrOptions?: number | SnackbarShowOptions
  ) => {
    let duration = 6000;
    let position: SnackbarPosition = 'default';
    if (typeof durationOrOptions === 'number') {
      duration = durationOrOptions;
    } else if (durationOrOptions !== undefined && typeof durationOrOptions === 'object') {
      duration = durationOrOptions.duration ?? 6000;
      position = durationOrOptions.position ?? 'default';
    }
    setSnackbar({
      open: true,
      message,
      severity,
      duration,
      position,
    });
  }, []);

  const hideSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false, position: 'default' }));
  }, []);

  const hideConfirmDialog = useCallback(() => {
    setConfirmDialog((prev) => ({ ...prev, open: false }));
  }, []);

  const showConfirmDialog = useCallback((
    title: string,
    message: string,
    onConfirm: () => void,
    onCancel = () => hideConfirmDialog(),
    options?: { confirmText?: string; cancelText?: string; severity?: ConfirmDialog['severity'] }
  ) => {
    setConfirmDialog({
      open: true,
      title,
      message,
      onConfirm: () => {
        onConfirm();
        hideConfirmDialog();
      },
      onCancel: () => {
        onCancel();
        hideConfirmDialog();
      },
      confirmText: options?.confirmText || 'Confirm',
      cancelText: options?.cancelText || 'Cancel',
      severity: options?.severity,
    });
  }, [hideConfirmDialog]);

  const showLoading = useCallback((message?: string) => {
    setLoading(true);
    if (message) {
      setLoadingMessage(message);
    }
  }, []);

  // Changes the overlay copy without opening or closing it. A call before
  // showLoading does not reveal the overlay.
  const updateLoadingMessage = useCallback((message: string) => {
    setLoadingMessage((current) => (current === null ? current : message));
  }, []);

  const hideLoading = useCallback(() => {
    setLoading(false);
    setLoadingMessage(null);
  }, []);

  const value = useMemo(
    () => ({
      loading,
      snackbar,
      confirmDialog,
      setLoading,
      showLoading,
      updateLoadingMessage,
      hideLoading,
      showSnackbar,
      hideSnackbar,
      showConfirmDialog,
      hideConfirmDialog,
    }),
    [
      loading,
      snackbar,
      confirmDialog,
      showLoading,
      updateLoadingMessage,
      hideLoading,
      showSnackbar,
      hideSnackbar,
      showConfirmDialog,
      hideConfirmDialog,
    ]
  );

  return (
    <UIContext.Provider value={value}>
      {children}
      {confirmDialog.open ? (
        <ConfirmDialogModal
          title={confirmDialog.title}
          message={confirmDialog.message}
          confirmText={confirmDialog.confirmText}
          cancelText={confirmDialog.cancelText}
          severity={confirmDialog.severity}
          onConfirm={confirmDialog.onConfirm}
          onCancel={confirmDialog.onCancel}
        />
      ) : null}
      {loadingMessage ? <LoadingOverlay message={loadingMessage} /> : null}
    </UIContext.Provider>
  );
};

export default UIContext;
